const OPERATION_NAMES = [
    'small_direct_input',
    'paste',
    'drop',
    'composition',
    'replacement',
    'unknown',
];

const CAPABILITY_VALUE_DELTA = 1;
const CAPABILITY_BEFORE_INPUT = 2;
const CAPABILITY_PASTE = 4;
const CAPABILITY_DROP = 8;
const CAPABILITY_COMPOSITION = 16;
const CAPABILITY_REPLACEMENT = 32;
const MAX_ACTIVE_EDITING_MS = 86400000;
const MAX_SUBMIT_DELAY_MS = 3600000;
const MAX_ACTIVE_GAP_MS = 30000;
const MAX_CODE_POINTS = 65000;
const MAX_CHANGED_CODE_POINTS = 100000;
const MAX_EVENTS = 20000;

function codePoints(value) {
    return Array.from(String(value ?? ''));
}

function codePointLength(value) {
    let length = 0;
    for (const ignored of String(value ?? '')) {
        length += 1;
    }
    return length;
}

function changedLengths(previousValue, nextValue) {
    const previous = codePoints(previousValue);
    const next = codePoints(nextValue);
    let prefix = 0;
    while (prefix < previous.length && prefix < next.length && previous[prefix] === next[prefix]) {
        prefix += 1;
    }

    let suffix = 0;
    while (
        suffix < previous.length - prefix
        && suffix < next.length - prefix
        && previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]
    ) {
        suffix += 1;
    }

    return {
        inserted: next.length - prefix - suffix,
        deleted: previous.length - prefix - suffix,
    };
}

function captureChangeHint(element, previousValue, inputType = '') {
    const selectionStart = element?.selectionStart;
    const selectionEnd = element?.selectionEnd;
    if (!Number.isInteger(selectionStart) || !Number.isInteger(selectionEnd)) {
        return null;
    }
    if (selectionStart < 0 || selectionEnd < selectionStart || selectionEnd > previousValue.length) {
        return null;
    }

    return {previousValue, selectionStart, selectionEnd, inputType};
}

function changedLengthsFromHint(previousValue, nextValue, hint) {
    if (!hint || hint.previousValue !== previousValue) {
        return null;
    }

    const {selectionStart, selectionEnd, inputType} = hint;
    if (!inputType.startsWith('insert') && !inputType.startsWith('delete')) {
        return null;
    }
    const selectedCodeUnits = selectionEnd - selectionStart;
    const insertedCodeUnits = nextValue.length - (previousValue.length - selectedCodeUnits);
    if (insertedCodeUnits >= 0) {
        if (selectedCodeUnits === 0 && sourceForInputType(inputType, false) === 'replacement') {
            return null;
        }

        return {
            inserted: codePointLength(nextValue.slice(selectionStart, selectionStart + insertedCodeUnits)),
            deleted: codePointLength(previousValue.slice(selectionStart, selectionEnd)),
        };
    }

    if (selectedCodeUnits !== 0 || !inputType.startsWith('delete')) {
        return null;
    }

    const deletedCodeUnits = -insertedCodeUnits;
    const deletesForward = inputType.includes('Forward');
    const deletedStart = deletesForward
        ? selectionStart
        : Math.max(0, selectionStart - deletedCodeUnits);
    const deletedEnd = deletesForward
        ? Math.min(previousValue.length, selectionStart + deletedCodeUnits)
        : selectionStart;

    return {
        inserted: 0,
        deleted: codePointLength(previousValue.slice(deletedStart, deletedEnd)),
    };
}

function createOperations() {
    return Object.fromEntries(OPERATION_NAMES.map((name) => [name, {
        events: 0,
        inserted: 0,
        largest: 0,
    }]));
}

function cloneState(state) {
    return {
        ...state,
        operations: Object.fromEntries(OPERATION_NAMES.map((name) => [name, {...state.operations[name]}])),
    };
}

function createUuid() {
    const cryptography = globalThis.crypto;
    if (cryptography?.randomUUID) {
        return cryptography.randomUUID().toLowerCase();
    }
    if (typeof cryptography?.getRandomValues !== 'function') {
        return null;
    }

    const bytes = new Uint8Array(16);
    cryptography.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function sourceForInputType(inputType, composing) {
    if (composing || inputType === 'insertCompositionText' || inputType === 'insertFromComposition') {
        return 'composition';
    }
    if (inputType === 'insertFromPaste' || inputType === 'insertFromPasteAsQuotation') {
        return 'paste';
    }
    if (inputType === 'insertFromDrop') {
        return 'drop';
    }
    if (inputType === 'insertReplacementText' || inputType === 'insertFromYank') {
        return 'replacement';
    }
    if (inputType === 'insertText' || inputType === 'insertLineBreak' || inputType === 'insertParagraph') {
        return 'small_direct_input';
    }
    return 'unknown';
}

function applyChange(state, previousValue, nextValue, source, at, knownChange = null) {
    if (previousValue === nextValue) {
        return false;
    }
    const change = knownChange || changedLengths(previousValue, nextValue);
    if (change.inserted === 0 && change.deleted === 0) {
        return false;
    }

    if (state.lastInputAt !== null) {
        state.activeEditingMs = Math.min(
            MAX_ACTIVE_EDITING_MS,
            state.activeEditingMs + Math.min(MAX_ACTIVE_GAP_MS, Math.max(0, at - state.lastInputAt)),
        );
    }
    state.lastInputAt = at;

    if (change.inserted > 0) {
        const operation = state.operations[source] || state.operations.unknown;
        operation.events += 1;
        operation.inserted += change.inserted;
        operation.largest = Math.max(operation.largest, change.inserted);
    }
    if (change.deleted > 0) {
        state.deletionEvents += 1;
        state.deletedCodePoints += change.deleted;
    }
    state.currentCodePoints += change.inserted - change.deleted;

    const changed = change.inserted + change.deleted;
    const isTransfer = change.inserted > 0 && ['paste', 'drop', 'unknown'].includes(source);
    if (isTransfer && change.inserted >= state.largestTransfer) {
        state.largestTransfer = change.inserted;
        state.eventsAfterLargestTransfer = 0;
        state.codePointsAfterLargestTransfer = 0;
    } else if (state.largestTransfer > 0) {
        state.eventsAfterLargestTransfer += 1;
        state.codePointsAfterLargestTransfer += changed;
    }

    return true;
}

function stateIsWithinBounds(state) {
    const operations = Object.values(state.operations);
    const insertedTotal = operations.reduce((total, operation) => total + operation.inserted, 0);
    return state.initialCodePoints <= MAX_CODE_POINTS
        && state.currentCodePoints <= MAX_CODE_POINTS
        && state.currentCodePoints >= 0
        && state.deletionEvents <= MAX_EVENTS
        && state.deletedCodePoints <= MAX_CHANGED_CODE_POINTS
        && state.eventsAfterLargestTransfer <= MAX_EVENTS
        && state.codePointsAfterLargestTransfer <= MAX_CHANGED_CODE_POINTS
        && insertedTotal <= MAX_CHANGED_CODE_POINTS
        && operations.every((operation) => (
            operation.events <= MAX_EVENTS
            && operation.inserted <= MAX_CHANGED_CODE_POINTS
            && operation.largest <= MAX_CODE_POINTS
        ));
}

function detectedCapabilities(element) {
    let capabilities = CAPABILITY_VALUE_DELTA | CAPABILITY_PASTE | CAPABILITY_DROP | CAPABILITY_COMPOSITION;
    if ('onbeforeinput' in element) {
        capabilities |= CAPABILITY_BEFORE_INPUT | CAPABILITY_REPLACEMENT;
    }
    return capabilities;
}

export class TextCompositionTracker {
    constructor({
        initialValue = '',
        baseline = 'empty',
        coverage = 'complete_session',
        uuidFactory = createUuid,
        now = () => performance.now(),
        capabilities = null,
    } = {}) {
        this.uuidFactory = uuidFactory;
        this.now = now;
        this.element = null;
        this.listeners = [];
        this.capabilitiesOverride = capabilities;
        this.capabilities = capabilities ?? CAPABILITY_VALUE_DELTA;
        this.syntheticDepth = 0;
        this.rebase(initialValue, baseline, coverage);
    }

    attach(element) {
        this.detach();
        this.element = element;
        this.capabilities = this.capabilitiesOverride ?? detectedCapabilities(element);
        this.currentValue = String(element.value ?? '');

        this.listen('beforeinput', (event) => {
            this.pendingSource = sourceForInputType(event.inputType, event.isComposing || this.composing);
            if (!this.composing && !event.isComposing) {
                this.pendingChangeHint = captureChangeHint(this.element, this.currentValue, event.inputType);
            }
        });
        this.listen('paste', () => {
            this.pendingSource = 'paste';
            this.pendingChangeHint = captureChangeHint(this.element, this.currentValue, 'insertFromPaste');
        });
        this.listen('drop', () => {
            this.pendingSource = 'drop';
            this.pendingChangeHint = captureChangeHint(this.element, this.currentValue, 'insertFromDrop');
        });
        this.listen('compositionstart', () => {
            this.composing = true;
            this.pendingChangeHint = captureChangeHint(this.element, this.currentValue, 'insertCompositionText');
        });
        this.listen('compositionend', () => {
            this.composing = false;
            const nextValue = String(this.element?.value ?? this.currentValue);
            this.recordValue(
                nextValue,
                'composition',
                changedLengthsFromHint(this.currentValue, nextValue, this.pendingChangeHint),
            );
            this.pendingSource = null;
            this.pendingChangeHint = null;
        });
        this.listen('input', (event) => {
            if (this.syntheticDepth > 0 || this.composing || event.isComposing) {
                return;
            }
            const nextValue = String(this.element?.value ?? '');
            this.recordValue(
                nextValue,
                this.pendingSource || 'unknown',
                changedLengthsFromHint(this.currentValue, nextValue, this.pendingChangeHint),
            );
            this.pendingSource = null;
            this.pendingChangeHint = null;
        });

        return this;
    }

    detach() {
        if (this.element) {
            this.listeners.forEach(([name, listener]) => this.element.removeEventListener(name, listener));
        }
        this.listeners = [];
        this.element = null;
    }

    rebase(value, baseline = 'unknown', coverage = 'complete_session') {
        const initialValue = String(value ?? '');
        const initialCodePoints = codePointLength(initialValue);
        this.currentValue = initialValue;
        this.baseline = baseline;
        this.coverage = coverage;
        this.state = {
            observationId: this.uuidFactory(),
            initialCodePoints,
            currentCodePoints: initialCodePoints,
            operations: createOperations(),
            deletionEvents: 0,
            deletedCodePoints: 0,
            largestTransfer: 0,
            eventsAfterLargestTransfer: 0,
            codePointsAfterLargestTransfer: 0,
            activeEditingMs: 0,
            lastInputAt: null,
            exhausted: initialCodePoints > MAX_CODE_POINTS,
        };
        this.revision = 0;
        this.pendingSource = null;
        this.pendingChangeHint = null;
        this.composing = false;
    }

    discard(value = '', baseline = 'empty') {
        this.rebase(value, baseline);
    }

    setValueWithoutObservation(value, baseline = 'restored_draft') {
        this.syntheticDepth += 1;
        try {
            this.rebase(value, baseline);
        } finally {
            this.syntheticDepth -= 1;
        }
    }

    recordValue(nextValue, source = 'unknown', change = null) {
        const value = String(nextValue ?? '');
        if (this.currentValue === value) {
            return;
        }
        if (this.state.exhausted) {
            this.currentValue = value;
            this.revision += 1;
            return;
        }
        if (applyChange(this.state, this.currentValue, value, source, this.now(), change)) {
            this.revision += 1;
            this.state.exhausted = !stateIsWithinBounds(this.state);
        }
        this.currentValue = value;
    }

    freezeAttempt(submittedValue = this.currentValue, previousAttempt = null) {
        const value = String(submittedValue ?? '');
        if (previousAttempt
            && previousAttempt.submittedValue === value
            && previousAttempt.revision === this.revision
        ) {
            return previousAttempt;
        }
        if (previousAttempt) {
            this.state.observationId = this.uuidFactory();
        }
        return Object.freeze({
            submittedValue: value,
            revision: this.revision,
            serialized: this.snapshot(value),
        });
    }

    snapshot(submittedValue = this.currentValue) {
        if (typeof this.state.observationId !== 'string' || this.state.exhausted) {
            return null;
        }

        const value = String(submittedValue ?? '');
        const snapshot = cloneState(this.state);
        const coverage = this.composing ? 'partial_session' : this.coverage;
        applyChange(snapshot, this.currentValue, value, 'unknown', snapshot.lastInputAt ?? this.now());

        const submittedAt = this.now();
        const lastInputToSubmit = snapshot.lastInputAt === null
            ? 0
            : Math.min(MAX_SUBMIT_DELAY_MS, Math.max(0, submittedAt - snapshot.lastInputAt));

        const payload = {
            version: 1,
            observation_id: snapshot.observationId,
            tracker_version: 1,
            baseline: this.baseline,
            coverage,
            initial_code_points: snapshot.initialCodePoints,
            final_code_points: snapshot.currentCodePoints,
            operations: snapshot.operations,
            deletion_events: snapshot.deletionEvents,
            deleted_code_points: snapshot.deletedCodePoints,
            observed_events_after_largest_transfer: snapshot.eventsAfterLargestTransfer,
            observed_code_points_changed_after_largest_transfer: snapshot.codePointsAfterLargestTransfer,
            active_editing_ms: Math.round(snapshot.activeEditingMs),
            last_input_to_submit_ms: Math.round(lastInputToSubmit),
            capabilities: this.capabilities,
        };

        if (!stateIsWithinBounds(snapshot)) {
            return null;
        }

        return JSON.stringify(payload);
    }

    listen(name, listener) {
        this.element.addEventListener(name, listener);
        this.listeners.push([name, listener]);
    }
}

export function createTextCompositionTracker(options) {
    return new TextCompositionTracker(options);
}
